import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { isSessionInvalidError, isTokenInvalidError } from '@/utils/authError'

type ViteEnv = { VITE_API_BASE_URL?: string; VITE_BFF_BASE?: string; VITE_DEV_USE_BFF?: string }

const resolveBaseUrl = (): string => {
  const env = (import.meta as unknown as { env: ViteEnv }).env
  const useBff = env.VITE_DEV_USE_BFF === 'true'
  const fromEnv = env.VITE_API_BASE_URL?.trim()
  const normalize = (url: string) => url.replace(/\/$/, '')
  if (useBff) {
    const bffBase = (env.VITE_BFF_BASE || 'http://127.0.0.1:8080').replace(/\/$/, '')
    return `${bffBase}/bff/api`
  }
  if (fromEnv) return normalize(fromEnv)
  return ''
}

const API_BASE_URL = resolveBaseUrl()
const isAuthEndpoint = (url?: string) => {
  if (!url) {
    return false
  }
  const target = String(url)
  return target.includes('/auth/login') || target.includes('/auth/logout') || target.includes('/auth/refresh')
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // 在 BFF 模式下不在浏览器注入 Authorization，改由 BFF 代理注入
    const useBff = ((import.meta as any).env?.VITE_DEV_USE_BFF === 'true')
    if (!useBff) {
      const token = localStorage.getItem('token')
      if (token) {
        (config.headers as any).Authorization = `Bearer ${token}`
      }
    }

    // Add study context header if study exists (exclude login/auth endpoints)
    // 浏览器不再发送 X-Current-*，由 BFF 注入

    // 在 BFF 模式下，移除 /api/v1 前缀，避免路径重复
    if (useBff && typeof config.url === 'string') {
      config.url = config.url.replace(/^\/api\/v1/, '')
    }

    // If sending FormData, let the browser set the multipart boundary automatically
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers) {
        delete (config.headers as any)['Content-Type']
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors and token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config

    // Skip token refresh for auth-related endpoints
    if (isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error)
    }

    // If auth error and we haven't tried to refresh the token yet
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      const isTokenError = isTokenInvalidError(error.response?.data, originalRequest.url)
      const authStore = useAuthStore()

      if (!isSessionInvalidError(error.response?.data, error.response?.status, originalRequest.url)) {
        return Promise.reject(error)
      }

      if ((import.meta as any).env?.VITE_DEV_USE_BFF === 'true') {
        await authStore.redirectToLogin('session_expired')
        return Promise.reject(error)
      }

      if (!isTokenError && error.response?.status === 403) {
        await authStore.redirectToLogin('session_expired')
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        // Try to refresh the token (非 BFF 模式)
        const refreshed = await authStore.refreshToken()
        if (refreshed) {
          originalRequest.headers.Authorization = `Bearer ${authStore.token}`
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
      }
      await authStore.redirectToLogin('token_refresh_failed')
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export default apiClient
